import * as React from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void | Promise<void>;
}

/**
 * Declarative confirm dialog. Use this when the page already controls
 * the open state (e.g. a "delete project" dialog tied to a row click).
 *
 * For one-shot replacements of window.confirm() inside an event handler,
 * use the imperative useConfirm() hook below.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
}: ConfirmDialogProps) {
  const [busy, setBusy] = React.useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!busy ? onOpenChange(next) : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {body && <DialogDescription asChild><div>{body}</div></DialogDescription>}
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={busy}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -- Imperative API -----------------------------------------------------------

interface ConfirmInput {
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

type Resolver = (result: boolean) => void;

interface ConfirmContextValue {
  request: (input: ConfirmInput) => Promise<boolean>;
}

const ConfirmContext = React.createContext<ConfirmContextValue | null>(null);

// We expose the imperative form via context (rather than props) because the
// alternative — passing a confirm fn through every page — would either bloat
// the AppLayout API or force consumers to mount their own dialog instances.
// Context gives us a single shared dialog the whole tree can borrow.
export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<ConfirmInput | null>(null);
  const resolverRef = React.useRef<Resolver | null>(null);
  const [busy, setBusy] = React.useState(false);

  const request = React.useCallback((input: ConfirmInput) => {
    return new Promise<boolean>((resolve) => {
      // If a confirm was already in flight, treat it as cancelled before
      // showing the new one — avoids stuck Promises if a caller fires twice.
      if (resolverRef.current) {
        resolverRef.current(false);
      }
      resolverRef.current = resolve;
      setState(input);
    });
  }, []);

  const close = (result: boolean) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    setBusy(false);
    resolver?.(result);
  };

  const handleConfirm = async () => {
    setBusy(true);
    // The promise resolves true and the caller does the actual work. We
    // don't await anything here because the imperative API doesn't take
    // an onConfirm — the caller is responsible for their own side-effect.
    close(true);
  };

  const handleCancel = () => {
    if (busy) return;
    close(false);
  };

  const value = React.useMemo<ConfirmContextValue>(() => ({ request }), [request]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog open={state !== null} onOpenChange={(next) => (!next ? handleCancel() : undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{state?.title}</DialogTitle>
            {state?.body && (
              <DialogDescription asChild>
                <div>{state.body}</div>
              </DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={handleCancel} disabled={busy}>
              {state?.cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              variant={state?.destructive ? 'destructive' : 'default'}
              onClick={handleConfirm}
              disabled={busy}
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {state?.confirmLabel ?? 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

/**
 * Drop-in replacement for window.confirm() with a styled dialog. Returns a
 * Promise that resolves true when the user confirms, false otherwise.
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: 'Archive project?', destructive: true });
 *   if (!ok) return;
 */
export function useConfirm() {
  const ctx = React.useContext(ConfirmContext);
  return React.useCallback(
    (input: ConfirmInput) => {
      if (!ctx) {
        // Fallback to native confirm if no provider is mounted — keeps tests
        // and isolated component renders working without forcing wrapping.
        return Promise.resolve(window.confirm(input.title));
      }
      return ctx.request(input);
    },
    [ctx],
  );
}
